<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSidebarContext } from './provider';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
  isPanelOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const { t } = useI18n();
const { resolvePath } = useSidebarContext();
const route = useRoute();

const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

// Item `shortcut` aponta pra página que mora em OUTRO grupo (ex.: Relatório de
// Pausas dentro de Telefonia): não acende este ícone, senão dois ficam ativos.
const isDescendantActive = items =>
  items.some(child => {
    if (child.shortcut) return false;
    if (child.to && route.path === resolvePath(child.to)) return true;
    if (child.activeOn?.includes(route.name)) return true;
    if (child.children) return isDescendantActive(child.children);
    return false;
  });

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;
    if (props.activeOn.includes(route.name)) return true;
  }
  if (hasChildren.value) {
    return isDescendantActive(props.children);
  }
  return false;
});

const showBadge = computed(() => {
  if (!props.getterKeys?.badge) return false;
  try {
    const getter = useMapGetter(props.getterKeys.badge);
    return getter.value;
  } catch {
    return false;
  }
});

// Badge com NÚMERO (getterKeys.count → { count, tone }): a Central de avisos
// precisa dizer quantos, não só "tem algo". Zero = sem badge.
// Par sólido 11 + texto 1 da mesma escala: os dois tokens invertem juntos com
// o tema, então o contraste vale nos dois (WCAG, texto de 10px pede ≥ 4,5:1).
// ruby-11/ruby-1 = 5,3:1 claro e 8,8:1 escuro (ruby-9/ruby-3 dava 3,4:1 no
// claro); amber-11/amber-1 = 4,5:1 claro e 12,2:1 escuro (amber-9/slate-12
// dava 1,4:1 no escuro — o slate-12 clareia e o amber-9 não muda).
const COUNT_TONES = {
  critical: 'bg-n-ruby-11 text-n-ruby-1',
  warning: 'bg-n-amber-11 text-n-amber-1',
};

const countGetter = props.getterKeys?.count
  ? useMapGetter(props.getterKeys.count)
  : null;

const countBadge = computed(() => {
  const badge = countGetter?.value;
  if (!badge?.count) return null;
  return {
    count: badge.count,
    text: badge.count > 99 ? '99+' : String(badge.count),
    tone: COUNT_TONES[badge.tone] || COUNT_TONES.warning,
  };
});

// O ícone não tem texto: o leitor de tela lê o rótulo e, com badge, quantos
// (o número inteiro, não o "99+" visual).
const ariaLabel = computed(() =>
  countBadge.value
    ? t(
        'SIDEBAR.COUNT_BADGE_ARIA',
        { label: props.label, count: countBadge.value.count },
        countBadge.value.count
      )
    : props.label
);

const handleClick = () => emit('toggle', props.name);
</script>

<template>
  <li class="relative" :data-sidebar-item="name">
    <RouterLink
      v-if="to && !hasChildren"
      v-tooltip.right="label"
      :to="to"
      :aria-label="ariaLabel"
      class="group flex items-center justify-center transition-colors rounded-lg size-10"
      :class="
        isActive
          ? 'bg-n-solid-4 text-n-blue-11'
          : 'text-n-slate-11 hover:text-n-green-11 hover:bg-n-green-3'
      "
    >
      <span
        v-if="typeof icon === 'string'"
        class="size-5 flex-shrink-0 transition-transform duration-150 ease-out group-hover:scale-110"
        :class="[icon]"
      />
      <span
        v-if="showBadge"
        class="absolute rounded-full size-2 bg-n-blue-9 top-1.5 right-1.5"
      />
      <span
        v-if="countBadge"
        class="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold leading-4 text-center tabular-nums"
        :class="countBadge.tone"
        aria-hidden="true"
        data-test-id="sidebar-count-badge"
      >
        {{ countBadge.text }}
      </span>
    </RouterLink>
    <button
      v-else
      v-tooltip.right="label"
      :aria-label="ariaLabel"
      class="group relative flex items-center justify-center transition-colors rounded-lg size-10"
      :class="
        isActive || isPanelOpen
          ? 'bg-n-solid-4 text-n-blue-11'
          : 'text-n-slate-11 hover:text-n-green-11 hover:bg-n-green-3'
      "
      @click="handleClick"
    >
      <span
        v-if="typeof icon === 'string'"
        class="size-5 flex-shrink-0 transition-transform duration-150 ease-out group-hover:scale-110"
        :class="[icon]"
      />
      <span
        v-if="showBadge"
        class="absolute rounded-full size-2 bg-n-blue-9 top-1.5 right-1.5"
      />
      <!-- Item com submenu (ex.: Chamados) também mostra o número. -->
      <span
        v-if="countBadge"
        class="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold leading-4 text-center tabular-nums"
        :class="countBadge.tone"
        aria-hidden="true"
        data-test-id="sidebar-count-badge"
      >
        {{ countBadge.text }}
      </span>
    </button>
  </li>
</template>
